import { AgBytesAPI } from "@ai360/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { getLastUsedLocationGuid, getTheUserCompanyGuid } from "~/login/selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

export const IS_SELECTABLE = "isSelectable";

/**
 * Not folded into `activeYn` -- Org Level Products needs inactive products still visible. Hiding is
 * cosmetic either way (`optionIsHiddenKey` doesn't block keyboard/typeahead selection), so surfaces
 * still need their own marking and Save-block.
 */
export const computeIsSelectable = (
    activeYn: boolean,
    isAgvanceLinked: boolean | null,
    showInactive: boolean = false
): boolean => (showInactive || activeYn) && isAgvanceLinked !== false;

const EMPTY_PRODUCTS: AgBytesAPI.IProductSelectionListItem[] = [];
const EMPTY_FILTERS: AgBytesAPI.IProductSelectionListFilters = {};
const EMPTY_GUIDS: string[] = [];

// Loader's own fadein (loader.css) is transparent for the first 250ms and doesn't reach full
// opacity until 500ms -- a fetch that resolves faster than that mounts and unmounts the spinner
// without it ever becoming visible. Latch `true` for at least this long once it fires.
const MIN_FETCHING_DISPLAY_MS = 600;

const useLatchedFetching = (isFetching: boolean): boolean => {
    const [display, setDisplay] = useState(isFetching);
    const shownAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (isFetching) {
            shownAtRef.current = Date.now();
            setDisplay(true);
            return;
        }
        if (shownAtRef.current == null) {
            setDisplay(false);
            return;
        }
        const remaining = MIN_FETCHING_DISPLAY_MS - (Date.now() - shownAtRef.current);
        if (remaining <= 0) {
            shownAtRef.current = null;
            setDisplay(false);
            return;
        }
        const timeoutId = setTimeout(() => {
            shownAtRef.current = null;
            setDisplay(false);
        }, remaining);
        return () => clearTimeout(timeoutId);
    }, [isFetching]);

    return display;
};

export interface IUseProductListOptions {
    customerGuid?: string | null;
    filters?: AgBytesAPI.IProductSelectionListFilters;
    isEnabled?: boolean;
    orgLevelGuid?: string | null;
    /** Multiple Location Affiliations -- fetches each location and unions the results by
     * `productGuid`. Takes precedence over `orgLevelGuid`/`customerGuid` when non-empty. */
    orgLevelGuids?: (string | null | undefined)[];
    scope: AgBytesAPI.ProductSelectionListScope;
}

export interface IUseProductListResult {
    isFetching: boolean;
    products: AgBytesAPI.IProductSelectionListItem[];
}

/**
 * `true` beats everything (linked somewhere); a bare `false` never overrides `null` (preference
 * off/unresolved at that location isn't the same as "confirmed unlinked"). Only `false` at every
 * location stays `false`.
 */
const mergeIsAgvanceLinked = (a: boolean | null, b: boolean | null): boolean | null => {
    if (a === true || b === true) return true;
    if (a === null || b === null) return null;
    return false;
};

const mergeProductLists = (
    resultsByLocation: AgBytesAPI.IProductSelectionListItem[][]
): AgBytesAPI.IProductSelectionListItem[] => {
    const merged = new Map<string, AgBytesAPI.IProductSelectionListItem>();
    for (const rows of resultsByLocation) {
        for (const row of rows) {
            const existing = merged.get(row.productGuid);
            merged.set(
                row.productGuid,
                !existing
                    ? row
                    : {
                          ...existing,
                          isAgvanceLinked: mergeIsAgvanceLinked(
                              existing.isAgvanceLinked,
                              row.isAgvanceLinked
                          ),
                          linkageCheckDegraded:
                              existing.linkageCheckDegraded || row.linkageCheckDegraded,
                      }
            );
        }
    }
    return Array.from(merged.values());
};

/**
 * One RTK cache entry over `agbytes/product/v1.0/selection-list`. Use this instead of a per-surface
 * fetch, so identical arguments share one request.
 */
export const useProductList = ({
    customerGuid = null,
    filters = EMPTY_FILTERS,
    isEnabled = true,
    orgLevelGuid = null,
    orgLevelGuids,
    scope,
}: IUseProductListOptions): IUseProductListResult => {
    const dispatch = useAppDispatch();
    const isMultiLocation = orgLevelGuids != null;

    const isScopeResolvable = Boolean(customerGuid || orgLevelGuid);
    const { data: products = EMPTY_PRODUCTS, isFetching: isSingleFetching } =
        AgBytesAPI.useGetProductSelectionListQuery(
            {
                scope,
                filters,
                ...(customerGuid && { customerGuid }),
                ...(!customerGuid && orgLevelGuid != null && { orgLevelGuid }),
            },
            {
                skip: isMultiLocation || !isEnabled || !isScopeResolvable,
                refetchOnMountOrArgChange: true,
            }
        );

    const guidList = useMemo(
        () =>
            !isMultiLocation
                ? EMPTY_GUIDS
                : Array.from(
                      new Set(orgLevelGuids.filter((guid): guid is string => Boolean(guid)))
                  ).sort(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMultiLocation, (orgLevelGuids ?? []).join("|")]
    );
    const filtersKey = JSON.stringify(filters);

    useEffect(() => {
        if (!isMultiLocation || !isEnabled || guidList.length === 0) {
            return;
        }
        const subscriptions = guidList.map((guid) =>
            dispatch(
                AgBytesAPI.extendedAi360Api.endpoints.getProductSelectionList.initiate({
                    scope,
                    filters,
                    orgLevelGuid: guid,
                })
            )
        );
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isMultiLocation, isEnabled, guidList, scope, filtersKey]);

    const multiLocationResult = useAppSelector((state) => {
        if (!isMultiLocation || guidList.length === 0) {
            return { data: EMPTY_PRODUCTS, isFetching: false };
        }
        const selections = guidList.map((guid) =>
            AgBytesAPI.extendedAi360Api.endpoints.getProductSelectionList.select({
                scope,
                filters,
                orgLevelGuid: guid,
            })(state)
        );
        const anyPending = selections.some(
            (selection) => selection.isFetching || selection.isUninitialized
        );
        // Don't publish a partial union while any location is still in flight -- a product
        // linked only at a not-yet-resolved location would otherwise read as unlinked for the
        // brief window before every leg settles.
        return {
            data: anyPending
                ? EMPTY_PRODUCTS
                : mergeProductLists(
                      selections.map((selection) => selection.data ?? EMPTY_PRODUCTS)
                  ),
            isFetching: anyPending,
        };
    });

    const rawIsFetching = isMultiLocation ? multiLocationResult.isFetching : isSingleFetching;
    const isFetching = useLatchedFetching(rawIsFetching);

    return {
        isFetching,
        products: isMultiLocation ? multiLocationResult.data : products,
    };
};

/**
 * The logged-in user's own org level, for `UserHierarchy`-scoped product lists. Every such surface
 * should resolve it through this one selector -- two surfaces computing it differently would each get
 * their own cache entry instead of sharing one.
 */
export const getLoggedInUserProductScopeOrgLevelGuid = (state: unknown): string | null =>
    getLastUsedLocationGuid(state) || getTheUserCompanyGuid(state) || null;

export const useLoggedInUserProductScopeOrgLevelGuid = (): string | null =>
    useAppSelector(getLoggedInUserProductScopeOrgLevelGuid);

export interface IProductDropdownOption {
    activeYn: boolean;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useProductList()` result to the plain option shape `RestrictedProductList`/`ProductList`
 * expect.
 */
export const toProductDropdownOptions = (
    products: AgBytesAPI.IProductSelectionListItem[]
): IProductDropdownOption[] =>
    products.map((product) => ({
        activeYn: product.activeYn,
        guid: product.productGuid,
        id: String(product.productId),
        isAgvanceLinked: product.isAgvanceLinked,
        name: product.name,
    }));
